import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';

// Get complaints with filters and pagination
export const getComplaints = createAsyncThunk(
  'complaints/getComplaints',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filters).forEach((key) => {
        if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== "") {
          params.append(key, filters[key]);
        } else if (filters[key] && typeof filters[key] === 'number') {
          params.append(key, filters[key].toString());
        }
      });

      const queryString = params.toString();
      const url = `${process.env.REACT_APP_API_URL}/api/complaints${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axios.get(url, {
        headers: {
          Accept: "application/json",
        },
      });
      return response.data;
    } catch (err) {
      if (err.response) {
        return rejectWithValue(err.response.data);
      } else if (err.request) {
        return rejectWithValue({
          message: "Network error - no response from server",
          error: "NETWORK_ERROR",
        });
      } else {
        return rejectWithValue({
          message: err.message || "An unexpected error occurred",
          error: "UNKNOWN_ERROR",
        });
      }
    }
  }
);

// Get single complaint details
export const getComplaintDetails = createAsyncThunk(
  'complaints/getComplaintDetails',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/complaints/${id}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Create new complaint
export const createComplaint = createAsyncThunk(
  'complaints/createComplaint',
  async (complaintData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/complaints`,
        complaintData,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      toast.success('Complaint created successfully');
      return response.data;
    } catch (err) {
      toast.error('Failed to create complaint');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update complaint
export const updateComplaint = createAsyncThunk(
  'complaints/updateComplaint',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/complaints/${id}`,
        data,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      toast.success('Complaint updated successfully');
      return response.data;
    } catch (err) {
      toast.error('Failed to update complaint');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Delete complaint
export const deleteComplaint = createAsyncThunk(
  'complaints/deleteComplaint',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/complaints/${id}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );
      toast.success('Complaint deleted successfully');
      return id;
    } catch (err) {
      toast.error('Failed to delete complaint');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Get complaints statistics
export const getComplaintStats = createAsyncThunk(
  'complaints/getComplaintStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/complaints/stats`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Get complaints for specific order
export const getComplaintsByOrder = createAsyncThunk(
  'complaints/getComplaintsByOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/complaints/order/${orderId}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const initialState = {
  loading: false,
  complaints: [],
  currentComplaint: null,
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  },
  stats: null,
  orderComplaints: [],
  error: null,
  filters: {
    status: '',
    priority: '',
    complaint_type: '',
    customer_name: '',
    search: '',
    page: 1,
  },
};

const complaintsSlice = createSlice({
  name: 'complaints',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: '',
        priority: '',
        complaint_type: '',
        customer_name: '',
        search: '',
        page: 1,
      };
    },
    clearCurrentComplaint: (state) => {
      state.currentComplaint = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get complaints
      .addCase(getComplaints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getComplaints.fulfilled, (state, action) => {
        state.loading = false;
        state.complaints = action.payload.data || [];
        state.pagination = {
          current_page: action.payload.current_page || 1,
          last_page: action.payload.last_page || 1,
          per_page: action.payload.per_page || 15,
          total: action.payload.total || 0,
        };
        state.error = null;
      })
      .addCase(getComplaints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch complaints';
      })
      
      // Get complaint details
      .addCase(getComplaintDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getComplaintDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentComplaint = action.payload;
        state.error = null;
      })
      .addCase(getComplaintDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch complaint details';
      })
      
      // Create complaint
      .addCase(createComplaint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createComplaint.fulfilled, (state, action) => {
        state.loading = false;
        state.complaints.unshift(action.payload);
        state.error = null;
      })
      .addCase(createComplaint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to create complaint';
      })
      
      // Update complaint
      .addCase(updateComplaint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateComplaint.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.complaints.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.complaints[index] = action.payload;
        }
        if (state.currentComplaint?.id === action.payload.id) {
          state.currentComplaint = action.payload;
        }
        state.error = null;
      })
      .addCase(updateComplaint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update complaint';
      })
      
      // Delete complaint
      .addCase(deleteComplaint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteComplaint.fulfilled, (state, action) => {
        state.loading = false;
        state.complaints = state.complaints.filter(c => c.id !== action.payload);
        if (state.currentComplaint?.id === action.payload) {
          state.currentComplaint = null;
        }
        state.error = null;
      })
      .addCase(deleteComplaint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to delete complaint';
      })
      
      // Get complaint stats
      .addCase(getComplaintStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getComplaintStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
        state.error = null;
      })
      .addCase(getComplaintStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch complaint statistics';
      })
      
      // Get complaints by order
      .addCase(getComplaintsByOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getComplaintsByOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orderComplaints = action.payload;
        state.error = null;
      })
      .addCase(getComplaintsByOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch order complaints';
      });
  },
});

export const { setFilters, clearFilters, clearCurrentComplaint, clearError } = complaintsSlice.actions;
export default complaintsSlice;